import { action, computed, observable } from 'mobx';

import { defaultNotification } from './constants';

import { requestStore } from './requestStore';

import { UploadFile } from 'antd/lib/upload/interface';
import moment from 'moment';
import { Attachement, Importancia, Municipio, Notification, OrganismoOperador } from '../models';

export class NotificationStore {
    @observable
    notifications: Notification[] = [];

    @observable
    currentStep: number = 0;

    @observable
    isFetching: boolean = false;

    @observable
    kind: 'Pendientes' | 'Historial' = 'Pendientes';

    @observable
    mode: 'edit' | 'view' = 'view';

    @observable
    showDrawer: boolean = false;

    @observable
    seenDrawer: boolean = false;

    @observable
    selectedNotification: Notification = defaultNotification;

    @observable
    notificationDescription: string = '';

    @observable
    organismosList: OrganismoOperador[] = [];

    @observable
    municipalityList: Municipio[] = [];

    @observable
    selectedOrganismosList: OrganismoOperador[] = [];

    @observable
    selectedMunicipalityList: Municipio[] = [];

    @observable
    ejercicio: number = 0;

    @computed
    get notificationCardList() {
        const list = [defaultNotification];
        list.push(...this.notifications);
        return list;
    }

    @action
    async getNotifications(rol: string) {
        const response = await requestStore.getNotificacionesUsuario(rol, this.ejercicio);
        if (response) {
            this.notifications = response;
        }
    }

    @action
    changeMode(mode: 'edit' | 'view') {
        this.mode = mode;
    }

    @computed
    get noAttachementDescription() {
        return this.notificationDescription === '';
    }

    @computed
    get municipalities() {
        return this.municipalityList.map(municipio => municipio.nombre);
    }

    @computed
    get canEdit() {
        return window.location.pathname.toLocaleLowerCase().includes('notificaciones');
    }

    @computed
    get organismos() {
        return this.organismosList.map(organismo => organismo.nombreRol);
    }

    @computed
    get selectedOrganismos() {
        return this.selectedOrganismosList.map(oo => oo.nombreRol);
    }

    @computed
    get selectedMunicipalities() {
        return this.selectedMunicipalityList.map(municipio => municipio.nombre);
    }

    @computed
    get canMoveNextStep() {
        const { currentStep, selectedNotification } = this;
        const {
            descripcion,
            fechaFin,
            fechaInicio,
            titulo,
            archivosAdjuntos
        } = selectedNotification;

        if (currentStep === 0) {
            return (
                descripcion !== '' &&
                fechaFin !== '' &&
                fechaInicio !== '' &&
                moment(fechaInicio) <= moment(fechaFin)
            );
        }
        if (currentStep === 1) {
            return archivosAdjuntos.length > 0;
        }

        if (currentStep === 2) {
            return (
                titulo &&
                titulo !== '' &&
                (this.selectedMunicipalityList.length !== 0 ||
                    this.selectedOrganismosList.length !== 0)
            );
        }
    }

    @action
    async deleteNotification(id: number) {
        const index = this.notifications.findIndex(notification => notification.id === id);
        await requestStore.deleteNotification(id);
        if (index >= 0) {
            this.notifications.splice(index, 1);
        }
    }

   @action
    async ArchivarNotificacion(id: number) {
        const index = this.notifications.findIndex(notification => notification.id === id);
        await requestStore.archivarNotification(id);
        if (index >= 0) {
            this.notifications.splice(index, 1);
        } 
    }

    @action
    async nextStep() {
        this.isFetching = true;
        if (this.currentStep === 0) {
            await this.createNotification();
        }
        this.isFetching = false;
        this.currentStep++;
    }

    @action
    async createNotification() {
        const {
            descripcion,
            fechaFin,
            fechaInicio,
            titulo,
            importancia
        } = this.selectedNotification;
        const params = {
            titulo,
            descripcion,
            fechaInicio,
            fechaFin,
            importancia
        };
        const response = await requestStore.postNotificacion(params);
        if (response !== undefined) {
            Object.assign(this.selectedNotification, response);
        }
    }

    @action
    done = async () => {
        const newNotification = Object.assign({}, this.selectedNotification);
        await this.editNotification(newNotification);
        this.notifications.push(newNotification);
        this.reset();
    };

    @action
    editNotification = async (newNotification: Notification) => {
        const notificationId = newNotification.id;

        newNotification.listaAudienciaMunicipios = this.selectedMunicipalityList.map(municipio => {
            return {
                municipioId: municipio.id,
                notificacionId: notificationId
            };
        });

        newNotification.listaAudienciaOo = this.selectedOrganismosList.map(oo => {
            return {
                ooId: oo.ooId,
                notificacionId: notificationId
            };
        });
        await requestStore.putNotificaciones(notificationId, newNotification);
    };

    @action
    reset() {
        this.unselectNotification();
        this.selectedMunicipalityList = [];
        this.selectedOrganismosList = [];
        this.currentStep = 0;
        this.changeMode('view');
    }

    @action
    editTitle(titulo: string) {
        this.selectedNotification.titulo = titulo;
    }

    @action
    editImportancia(importancia: Importancia) {
        this.selectedNotification.importancia = importancia;
    }

    @action
    editDescription(descripcion: string) {
        this.selectedNotification.descripcion = descripcion;
    }

    @action
    editDates(fechaInicio: string, fechaFin: string) {
        this.selectedNotification.fechaInicio = fechaInicio;
        this.selectedNotification.fechaFin = fechaFin;
    }

    @action
    editNotificationDescription(description: string) {
        this.notificationDescription = description;
    }

    @action
    editMunicipalities(municipalities: string[]) {
        const listaAudienciaMunicipios = this.municipalityList.filter(m =>
            municipalities.find(municipality => municipality === m.nombre)
        );
        this.selectedMunicipalityList = listaAudienciaMunicipios;
    }

    @action
    editAudience(organismos: string[]) {
        const listaAudienciaOo = this.organismosList.filter(oo =>
            organismos.find(organismo => organismo === oo.nombreRol)
        );
        this.selectedOrganismosList = listaAudienciaOo;
    }

    @computed
    get selectedAllMunicipalities() {
        return this.selectedMunicipalityList.length === this.municipalities.length;
    }

    @computed
    get selectedAllOrganismos() {
        return this.selectedOrganismosList.length === this.organismos.length;
    }

    @action
    editAllMunicipalities(checked: boolean) {
        this.selectedNotification.audienciaTodosMunicipios = checked;
        if (checked) {
            this.selectedMunicipalityList.push(...this.municipalityList);
        } else {
            this.selectedMunicipalityList = [];
        }
    }

    @action
    editAllOrganismos(checked: boolean) {
        this.selectedNotification.audienciaTodosOo = checked;
        if (checked) {
            this.selectedOrganismosList.push(...this.organismosList);
        } else {
            this.selectedOrganismosList = [];
        }
    }

    @action
    addAttachements(file: UploadFile) {
        const descripcion = this.notificationDescription;
        const notificacionId = this.selectedNotification.id;
        const contentTypeHeader = file.type;
        const nombre = file.response.nombre;
        this.selectedNotification.archivosAdjuntos.push({
            nombre,
            contentTypeHeader,
            notificacionId,
            descripcion,
            ...file
        });
        this.notificationDescription = '';
    }

    @action
    removeAttachement(file: UploadFile) {
        const { archivosAdjuntos } = this.selectedNotification;
        const index = archivosAdjuntos.findIndex(attachement => attachement.uid === file.uid);
        this.selectedNotification.archivosAdjuntos.splice(index, 1);
    }

    @action
    async getAttachement(file: Attachement) {
        const response = await requestStore.getArchivo(file.nombre, file.notificacionId);
        if (response) {
            this.selectedNotification.leida = true;
        }
        return response;
    }

    @action
    async deleteAttachement(fileName: string) {
        const { id } = this.selectedNotification;
        return await requestStore.deleteArchivo(fileName, id);
    }

    @action
    async getMunicipios() {
        const response = await requestStore.getMunicipios();
        if (response) {
            this.municipalityList = response;
        }
    }

    @action
    async getOrganismos() {
        const response = await requestStore.getOrganismoOperador();
        if (response) {
            this.organismosList = response;
        }
    }

    @action
    toggleDrawer() {
        if (!this.seenDrawer) {
            this.seenDrawer = true;
        }
        this.showDrawer = !this.showDrawer;
    }

    @action
    changeKind(kind: 'Pendientes' | 'Historial') {
        this.kind = kind;
    }

    @action
    selectNotification(notification: Notification) {
        this.selectedNotification = notification;
    }

    @action
    unselectNotification() {
        this.selectedNotification = defaultNotification;
    }

    @computed
    get openModal() {
        return this.selectedNotification.id !== defaultNotification.id || this.mode === 'edit';
    }

    @computed
    get currentNotificationCount() {
        return this.notificationsCurrent.length;
    }

    @computed
    get notificationsCurrent() {
        return this.notifications.filter(notification => !notification.leida);
    }

    @computed
    get notificationsPast() {
        return this.notifications.filter(notification => notification.leida);
    }

    @computed
    get notificationType() {
        const { importancia } = this.selectedNotification;
        return importancia === Importancia.Alto
            ? 'error'
            : importancia === Importancia.Medio
            ? 'warning'
            : 'info';
    }

    @action
    async getReporteNotificacion() {
        const response = await requestStore.getReporteNotificacion(this.selectedNotification!.id);
        return response;
    }
}

export const notificationStore = new NotificationStore();
export default notificationStore;
